
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  PlayIcon,
  EyeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import { getImageUrlWithEnv } from '../utils/imageUrl';

// YouTube URL utilities
const isYouTubeUrl = (url) => {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const VideoManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedViews, setUpdatedViews] = useState(new Set());
  const navigate = useNavigate();

  const categories = ['მატჩის ჰაილაითები', 'ინტერვიუები', 'ტრენინგი', 'ღონისძიებები', 'სკილები', 'ფანების ვიდეო'];

  useEffect(() => {
    loadVideos();
    
    // Set up periodic polling to refresh view counts every 15 seconds
    const interval = setInterval(() => {
      loadVideos(false);
    }, 15000);
    
    return () => clearInterval(interval);
  }, []);

  const loadVideos = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      
      const response = await fetch(getApiUrl('/api/videos/admin'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const videosData = await response.json();
        
        // Check for view count changes and highlight them
        if (!showLoader && videos.length > 0) {
          const newUpdatedViews = new Set();
          videosData.forEach(newVideo => {
            const oldVideo = videos.find(v => v.id === newVideo.id);
            if (oldVideo && oldVideo.views !== newVideo.views) {
              newUpdatedViews.add(newVideo.id);
            }
          });
          setUpdatedViews(newUpdatedViews);
          // Clear highlights after 5 seconds
          setTimeout(() => setUpdatedViews(new Set()), 5000);
        }
        
        setVideos(videosData);
      } else {
        console.error('Error loading videos:', response.statusText);
      }
      if (showLoader) setLoading(false);
    } catch (error) {
      console.error('Error loading videos:', error);
      if (showLoader) setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('ნამდვილად გსურთ ამ ვიდეოს წაშლა?')) {
      try {
        // Get token from the same source as apiService
        const storedUser = localStorage.getItem('fciveria_admin_user');
        const token = storedUser ? JSON.parse(storedUser).token : null;
        
        if (!token) {
          console.error('No auth token found');
          return;
        }
        
        const response = await fetch(getApiUrl(`/api/videos/${id}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          loadVideos();
        } else {
          console.error('Error deleting video:', response.statusText);
        }
      } catch (error) {
        console.error('Error deleting video:', error);
      }
    }
  };

  const togglePublish = async (video) => {
    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        return;
      }
      
      const response = await fetch(getApiUrl(`/api/videos/${video.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...video,
          isPublished: !video.isPublished
        })
      });
      
      if (response.ok) {
        loadVideos();
      } else {
        console.error('Error updating video:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating video:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader 
        title="ვიდეო გალერეის მართვა"
        subtitle="მართეთ ვიდეო კონტენტი"
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/videos/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          ახალი ვიდეო
        </button>
      </AdminHeader>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {videos.length === 0 ? (
            <div className="text-center py-12">
              <PlayIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 font-georgian">ვიდეოები არ არის</h3>
              <p className="mt-1 text-sm text-gray-500 font-georgian">დაიწყეთ ახალი ვიდეოს დამატებით</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/_panel-7c39f8/videos/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 font-georgian"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  ახალი ვიდეო
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {videos.map((video) => (
                <div key={video.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="relative">
                    <img
                      src={getImageUrlWithEnv(video.thumbnailUrl)}
                      alt={video.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                        <PlayIcon className="h-6 w-6 text-red-600 ml-1" />
                      </div>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        video.isPublished 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {video.isPublished ? 'გამოქვეყნებული' : 'არ არის გამოქვეყნებული'}
                      </span>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs font-georgian">
                      {video.duration}
                    </div>
                    {isYouTubeUrl(video.videoUrl) && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-georgian flex items-center">
                        <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                        YouTube
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 font-georgian mb-2 line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-xs text-gray-600 font-georgian mb-2 line-clamp-2">
                      {video.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 font-georgian mb-3">
                      <span className="bg-red-100 text-red-600 px-2 py-1 rounded">
                        {video.category}
                      </span>
                      <div className="flex items-center space-x-2">
                        <span className={`flex items-center transition-all duration-500 ${
                          updatedViews.has(video.id) 
                            ? 'text-green-600 font-bold scale-110' 
                            : ''
                        }`}>
                          <EyeIcon className="h-3 w-3 mr-1" />
                          {video.views}
                        </span>
                        <span className="flex items-center">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          {video.duration}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => togglePublish(video)}
                        className={`text-xs px-3 py-1 rounded font-georgian ${
                          video.isPublished
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : 'bg-green-100 text-green-600 hover:bg-green-200'
                        }`}
                      >
                        {video.isPublished ? 'დამალვა' : 'გამოქვეყნება'}
                      </button>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/_panel-7c39f8/videos/${video.id}/edit`)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(video.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <PlayIcon className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">სულ ვიდეოები</p>
                <p className="text-2xl font-bold text-gray-900">{videos.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <EyeIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">გამოქვეყნებული</p>
                <p className="text-2xl font-bold text-gray-900">
                  {videos.filter(v => v.isPublished).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">#</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">კატეგორიები</p>
                <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-yellow-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">👁</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">სულ ნახვები</p>
                <p className="text-2xl font-bold text-gray-900">
                  {videos.reduce((sum, v) => sum + v.views, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoManagement;
